import React, { useState, useEffect, useMemo, useRef } from "react";
import { Dropdown } from "antd";
import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import { createPortal } from "react-dom";
import SwitcherIcon from "../../../Assets/Icons/switcher.svg";
import ProfileIcon from "../../../Assets/Images/Profile.svg";
import "./sidebar.scss";
import { useNavigate } from "react-router-dom";
import Loader from "../Loader";
import { useDispatch, useSelector } from "react-redux";
import { clearAuth } from "../../../redux/slices/authSlice";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import { useLocation } from "react-router-dom";
import { getS3PublicUrl } from "../../../utils/s3";

const getBusinessId = (business) => business?._id || business?.id;

const getBusinessLogo = (business) =>
  business?.logo ||
  business?.logoUrl ||
  business?.profileImage ||
  business?.user?.logo ||
  business?.user?.profileImage ||
  "";

const isBusinessBlocked = (business) => {
  const status = String(business?.status || "").toLowerCase();
  return ["declined", "disabled", "deleted", "rejected"].includes(status);
};

const getBlockedBusinessMessage = (business) => {
  const status = String(business?.status || "").toLowerCase();

  if (status === "deleted") {
    return "Your Account got deleted. Please contact Support.";
  }

  return "Your Account is Disabled. Please contact Support for further information.";
};

const StatusBadge = ({ status }) => {
  let bgColor = "";
  let textColor = "";
  let text = "";

  switch (status) {
    case 'approved':
      bgColor = "bg-green-100";
      textColor = "text-green-700";
      text = "Live";
      break;
    case 'deleted':
      bgColor = "bg-gray-100";
      textColor = "text-gray-600";
      text = "Deleted";
      break;
    case 'declined':
      bgColor = "bg-red-100";
      textColor = "text-red-700";
      text = "Disabled";
      break;
    case 'pending':
      bgColor = "bg-yellow-100";
      textColor = "text-yellow-700";
      text = "Pending";
      break;
    default:
      return null;
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor} ml-2`}
      style={{ letterSpacing: 0.1 }}
    >
      {text}
    </span>
  );
};

const BusinessSwitcher = () => {
  const location = useLocation();
  const currentPath = location?.pathname;
  const [businesses, setBusinesses] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blockedBusiness, setBlockedBusiness] = useState(null);
  const [blockedBusinessAction, setBlockedBusinessAction] = useState(null);
  const navigate = useNavigate();
  const businessSwitcherRef = useRef(null);
  const hasInitializedSelectionRef = useRef(false);

  const dispatch = useDispatch();
  const selectedmerchant = useSelector(
    (state) => state.merchantReducer.selectedMerchent
  );
  const user = useSelector((state) => state.authReducer.tokenData?.user);

  const merchantProfilesFromRedux = useSelector((state) => state.merchantReducer.merchantProfiles);

  const validBusinesses = useMemo(() => {
    const profiles = merchantProfilesFromRedux?.data || user?.merchantProfiles || [];
    return [...profiles].sort((a, b) => {
      const aStatus = String(a?.status || "").toLowerCase();
      const bStatus = String(b?.status || "").toLowerCase();
      const aApproved = aStatus === "approved" ? 0 : 1;
      const bApproved = bStatus === "approved" ? 0 : 1;
      if (aApproved !== bApproved) return aApproved - bApproved;
      return String(a?.businessName || "").localeCompare(String(b?.businessName || ""));
    });
  }, [merchantProfilesFromRedux?.data, user?.merchantProfiles]);

  const sortedAndFilteredBusinesses = useMemo(() => {
    return validBusinesses;
  }, [validBusinesses]);

  const getFallbackBusiness = (businessList = [], currentBusiness = null) => {
    const businessesList = Array.isArray(businessList) ? businessList : [];
    return (
      businessesList.find(
        (biz) =>
          !isBusinessBlocked(biz) &&
          String(biz?.status || "").toLowerCase() === "approved"
      ) ||
      businessesList.find((biz) => !isBusinessBlocked(biz) && getBusinessId(biz) !== getBusinessId(currentBusiness)) ||
      null
    );
  };

  const showBlockedBusinessModal = (business, fallbackBusiness = null) => {
    setBlockedBusiness(business);
    setBlockedBusinessAction(fallbackBusiness ? "switch" : "logout");
  };

  useEffect(() => {
    console.log("Setting businesses and initial selection logic running...");
    setBusinesses(sortedAndFilteredBusinesses);

    if (sortedAndFilteredBusinesses.length > 0) {
      const storedSelectedBusinessId = localStorage.getItem('swoodle_selected_merchant_id');
      let businessToSet = null;

      if (storedSelectedBusinessId) {
        businessToSet = sortedAndFilteredBusinesses.find(
          (p) => getBusinessId(p) === storedSelectedBusinessId
        );
      }

      if (!businessToSet) {
        businessToSet = sortedAndFilteredBusinesses.find((p) => p.status === "approved");
      }

      if (!businessToSet && sortedAndFilteredBusinesses.length > 0) {
        businessToSet = sortedAndFilteredBusinesses[0];
      }

      if (businessToSet && getBusinessId(businessToSet) !== getBusinessId(selectedmerchant)) {
        if (isBusinessBlocked(businessToSet)) {
          const fallbackBusiness = getFallbackBusiness(sortedAndFilteredBusinesses, businessToSet);
          if (!hasInitializedSelectionRef.current) {
            if (fallbackBusiness && getBusinessId(fallbackBusiness) !== getBusinessId(selectedmerchant)) {
              dispatch(setCurrentBusiness(fallbackBusiness));
            } else {
              dispatch(setCurrentBusiness(null));
            }
          } else {
            showBlockedBusinessModal(businessToSet, fallbackBusiness);
            if (fallbackBusiness && getBusinessId(fallbackBusiness) !== getBusinessId(selectedmerchant)) {
              dispatch(setCurrentBusiness(fallbackBusiness));
            } else {
              dispatch(setCurrentBusiness(null));
            }
          }
        } else {
          console.log("Dispatching setCurrentBusiness:", businessToSet.businessName);
          dispatch(setCurrentBusiness(businessToSet));
        }
      } else if (!businessToSet && selectedmerchant !== null) {
        console.log("Clearing selected business as no valid business found.");
        dispatch(setCurrentBusiness(null));
        localStorage.removeItem('swoodle_selected_merchant_id');
      }
    } else {
      if (selectedmerchant !== null) {
        console.log("No profiles available, clearing selected business.");
        dispatch(setCurrentBusiness(null));
      }
      setBusinesses([]);
      localStorage.removeItem('swoodle_selected_merchant_id');
    }

    hasInitializedSelectionRef.current = true;
  }, [sortedAndFilteredBusinesses]);

  useEffect(() => {
    const selectedBusinessId = getBusinessId(selectedmerchant);
    if (selectedBusinessId) {
      localStorage.setItem('swoodle_selected_merchant_id', selectedBusinessId);
    } else {
      localStorage.removeItem('swoodle_selected_merchant_id');
    }
  }, [selectedmerchant]);

  // ADDED: Sync selectedMerchant from latest Redux state on profile update
  useEffect(() => {
    const storedSelectedBusinessId = localStorage.getItem('swoodle_selected_merchant_id');

    if (storedSelectedBusinessId && merchantProfilesFromRedux?.data?.length > 0) {
      const updatedBusiness = merchantProfilesFromRedux.data.find(
        (biz) => getBusinessId(biz) === storedSelectedBusinessId
      );

      if (
        updatedBusiness &&
        (
          getBusinessId(updatedBusiness) !== getBusinessId(selectedmerchant) ||
          updatedBusiness.businessName !== selectedmerchant?.businessName ||
          getBusinessLogo(updatedBusiness) !== getBusinessLogo(selectedmerchant)
        )
      ) {
        if (isBusinessBlocked(updatedBusiness)) {
          const fallbackBusiness = getFallbackBusiness(validBusinesses, updatedBusiness);
          showBlockedBusinessModal(updatedBusiness, fallbackBusiness);
          if (fallbackBusiness && getBusinessId(fallbackBusiness) !== getBusinessId(selectedmerchant)) {
            dispatch(setCurrentBusiness(fallbackBusiness));
          } else {
            dispatch(setCurrentBusiness(null));
          }
        } else {
          dispatch(setCurrentBusiness(updatedBusiness));
        }
      }
    }
  }, [merchantProfilesFromRedux?.data, selectedmerchant, validBusinesses, dispatch]);

  const handleBusinessSwitch = (biz) => {
    if (isBusinessBlocked(biz)) {
      const fallbackBusiness = getFallbackBusiness(validBusinesses, biz);
      showBlockedBusinessModal(biz, fallbackBusiness);
      if (fallbackBusiness && getBusinessId(fallbackBusiness) !== getBusinessId(selectedmerchant)) {
        dispatch(setCurrentBusiness(fallbackBusiness));
      } else {
        dispatch(setCurrentBusiness(null));
      }
      setDropdownVisible(false);
      return;
    }

    if (getBusinessId(selectedmerchant) !== getBusinessId(biz)) {
      setDropdownVisible(false);
      setLoading(true);

      dispatch(setCurrentBusiness(biz));
      setLoading(false);
    } else {
      setDropdownVisible(false);
    }
  };

  const menu = (
    <div
      className="business-switcher-dropdown"
      style={{
        background: "#fff",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        borderRadius: 10,
        width: "100%",
        maxWidth: "100%",
        maxHeight: 220,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ overflowY: "auto", flexGrow: 1, paddingBottom: '12px' }}>
        {businesses.length > 0 ? (
          businesses.map((biz) => (
            <div
              key={getBusinessId(biz)}
              className={`dropdown-item flex items-center px-3 py-2 ${selectedmerchant && getBusinessId(biz) === getBusinessId(selectedmerchant) ? "selected" : ""
                } ${isBusinessBlocked(biz) ? "opacity-60 cursor-pointer hover:bg-[#f5faff]" : "cursor-pointer hover:bg-[#f5faff]"}`}
              onClick={() => handleBusinessSwitch(biz)}
            >
              <img
                src={getS3PublicUrl(getBusinessLogo(biz)) || ProfileIcon}
                alt={biz?.businessName}
                className="business-logo w-9 h-9 rounded-full mr-3"
                width="36"
                height="36"
                loading="lazy"
                decoding="async"
                fetchpriority="low"
              />
              <div className="flex-1">
              <div className="business-name font-medium text-[15px] leading-tight">
                  {biz?.businessName}
                  <StatusBadge status={biz.status} />
                </div>
                <div className="business-email text-xs text-gray-400">
                  {user?.email}
                </div>
              </div>
              {selectedmerchant && getBusinessId(biz) === getBusinessId(selectedmerchant) && (
                <CheckOutlined className="text-[#00ADEF] ml-2" />
              )}
            </div>
          ))
        ) : (
          <div className="px-3 py-2 text-gray-500 text-sm">No businesses available.</div>
        )}
      </div>
      <div
        style={{
          width: "90%",
          margin: "0 auto",
          paddingTop: '12px',
          borderTop: '1px solid #eee',
          background: '#fff',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
        }}
      >
        <button
          className="create-business-btn flex items-center justify-center bg-[#00ADEF] text-white font-semibold text-[15px] h-[36px] rounded-[4px] transition-colors duration-150 hover:bg-[#0095c8] w-full mb-4"
          style={{
            boxShadow: "0 2px 8px rgba(0,173,239,0.10)",
            letterSpacing: 0.1,
            gap: 6,
          }}
          onClick={() => navigate("/merchant/getting-started")}
        >
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-white mr-1">
            <PlusOutlined className="text-[#00ADEF] text-[8px]" />
          </span>
          Create New Business
        </button>
      </div>
    </div>
  );

  return (
    <div className="business-switcher px-[20px] mb-3" ref={businessSwitcherRef}>
      {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
      <Dropdown
        overlay={menu}
        trigger={["click"]}
        placement="bottomLeft"
        overlayClassName="pb-4 business-switcher-dropdown-wrapper"
        getPopupContainer={(trigger) => {
          const sidebarContainer = trigger?.closest('.sidebar-container');
          return sidebarContainer || document.body;
        }}
        disabled={loading || sortedAndFilteredBusinesses.length === 0 || currentPath.includes("/merchant/offer-form")}
        open={dropdownVisible}
        onOpenChange={(flag) => setDropdownVisible(flag)}
      >
        <div
          className={`current-business flex items-center py-2 px-2 rounded hover:bg-[#f5faff] ${currentPath.includes("/merchant/offer-form")
            ? "cursor-not-allowed opacity-50"
            : "cursor-pointer"
            }`}
        >
          {selectedmerchant ? (
            <>
              <img
                src={getS3PublicUrl(getBusinessLogo(selectedmerchant)) || ProfileIcon}
                alt={selectedmerchant?.businessName}
                className="business-logo w-9 h-9 rounded-full mr-3 truncate"
                width="36"
                height="36"
                decoding="async"
                fetchpriority="high"
              />
              <div className="flex-1">
                <div className="flex business-name font-medium text-[15px] leading-tight whitespace-nowrap">
                  {(selectedmerchant?.businessName)?.length > 10
                    ? (selectedmerchant?.businessName).slice(0, 10) + "..."
                    : selectedmerchant?.businessName}
                </div>
                <div className="business-email text-xs text-gray-400">
                  {user?.email}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 text-gray-400">No business found</div>
          )}
          <img
            src={SwitcherIcon}
            alt="switcher"
            className="ml-2 w-7 h-7 object-contain"
          />
        </div>
      </Dropdown>
      {loading && <Loader />}
      {blockedBusiness && createPortal(
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-[560px] rounded-[20px] bg-white px-7 py-8 md:px-10 md:py-9 shadow-2xl">
            <div className="mb-5">
              <div className="text-[22px] md:text-[24px] font-semibold text-[#111827] leading-tight">
                Business unavailable
              </div>
              <div className="mt-3 text-[15px] md:text-[16px] leading-7 text-[#4B5563] max-w-[48ch]">
                {getBlockedBusinessMessage(blockedBusiness)}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="h-[42px] min-w-[110px] rounded-[10px] bg-[#00ADEF] px-6 text-[14px] font-semibold text-white hover:bg-[#0095c8]"
                onClick={() => {
                  const action = blockedBusinessAction;
                  setBlockedBusiness(null);
                  setBlockedBusinessAction(null);

                  if (action === "logout") {
                    dispatch(clearAuth());
                    localStorage.removeItem("swoodle_selected_merchant_id");
                    navigate("/");
                  }
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default BusinessSwitcher;
